import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Upload,
  Input,
  Button,
  List,
  Typography,
  Empty,
  message,
  Spin,
  theme,
} from "antd";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  UploadOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  SearchOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  Modal as RSModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import httpInjectorService from "../../services/http-injector.service";
import { NotificationContext } from "../../NotificationProvider";

const { Dragger } = Upload;
const { Text } = Typography;

const FileUploads = () => {
  const { token } = theme.useToken();
  const [files, setFiles] = useState([]);
  const [search, setSearch] = useState("");

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [loading, setLoading] = useState(true);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState(null);

  const { openNotification } = useContext(NotificationContext);
  const uploadRef = useRef(null);

  const uploadProps = {
    multiple: true,
    showUploadList: false,
    openFileDialogOnClick: false,

    customRequest: async ({ file, onSuccess, onError }) => {
      const formData = new FormData();
      formData.append("file", file);

      let simulatedProgress = 0;
      let progressInterval;

      try {
        setUploading(true);
        setUploadProgress(0);

        progressInterval = setInterval(() => {
          simulatedProgress += 10;

          if (simulatedProgress <= 95) {
            setUploadProgress(simulatedProgress);
          }
        }, 300);
        const response = await httpInjectorService.bulkUploadSheet(formData);
        console.log(response, "response")

        clearInterval(progressInterval);
        setUploadProgress(100);

        if (response.status === "SUCCESS") {
          openNotification("success", response.message);
          await getUsersheetList();

          onSuccess("ok");
        } else {
          openNotification("error", response.message);
          onError(new Error(response.message));
        }
      } catch (error) {
        console.error("Upload error:", error);
        message.error(`${file.name} failed to upload`);
        onError(error);
      } finally {
        clearInterval(progressInterval);

        setTimeout(() => {
          setUploading(false);
          setUploadProgress(0);
        }, 1000);
      }
    },
  };

  const getUsersheetList = async () => {
    setLoading(true);
    try {
      const response = await httpInjectorService.getUserSheets();

      if (response.status === "SUCCESS") {
        const formattedFiles = response.data.map((item) => ({
          uid: item.id.toString(),
          name: item.user_sheet_file_name,
          size: item.file_size || "--",
          type: item.user_sheet_file_name.includes(".pdf") ? "pdf" : "excel",
          uploadedDate: new Date(item.uploaded_date).toLocaleDateString(
            "en-GB",
            {
              day: "2-digit",
              month: "short",
              year: "numeric",
            },
          ),
        }));

        setFiles(formattedFiles);
      } else {
        setFiles([]);
      }
    } catch (error) {
      console.error("Failed to fetch files:", error);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedFileId(id);
    setIsDeleteModalOpen(true);
  };

  const cancelDelete = () => {
    setSelectedFileId(null);
    setIsDeleteModalOpen(false);
  };

  const proceedDelete = async () => {
    try {
      const response = await httpInjectorService.removeUserSheet({
        file_id: selectedFileId,
      });

      if (response.status === "SUCCESS") {
        openNotification("success", response.message);

        await getUsersheetList();

        cancelDelete();
      } else {
        openNotification("error", response.message);
      }
    } catch (error) {
      console.error("Delete error:", error);
      message.error("Error deleting file");
    }
  };

  useEffect(() => {
    getUsersheetList();
  }, []);

  const filteredFiles = files.filter((file) =>
    file.name.toLowerCase().includes(search.toLowerCase()),
  );

  const handleDownloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Template");

    const headers = [
      "FIRST NAME",
      "LAST NAME",
      "PASSPORT NUMBER",
      "NATIONAL INSURANCE NUMBER",
      "VERIFICATION REQUIRED"
    ];

    worksheet.columns = headers.map((header) => ({
      header: header,
      key: header.toLowerCase().replace(/ /g, "_"),
      width: 20,
    }));
    worksheet.views = [{ state: "frozen", ySplit: 1 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileName = "Sheet_Template.xlsx";
    saveAs(blob, fileName);
  };

  return (
    <div className="mt-4 user-management-uploads">
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "16px",
        }}
      >
        <Button
          icon={<DownloadOutlined />}
          onClick={handleDownloadTemplate}
          style={{
            backgroundColor: "#5167c1",
            color: "white",
          }}
        >
          Template
        </Button>
      </div>
      <Dragger
        className="user-upload-dragger"
        ref={uploadRef}
        {...uploadProps}
        style={{
          borderRadius: "10px",
          padding: "20px",
          background: token.colorFillAlter,
          borderColor: token.colorBorder,
        }}
      >
        <p className="ant-upload-drag-icon">
          <UploadOutlined style={{ fontSize: "32px", color: "#5167c1" }} />
        </p>

        <p
          style={{
            fontSize: "16px",
            fontWeight: 600,
            marginBottom: "5px",
          }}
        >
          Drag and drop your files
        </p>

        <Button
          type="primary"
          icon={<UploadOutlined />}
          style={{ marginTop: "10px" ,backgroundColor: "#5167c1",
            color: "white",}}
          onClick={() =>
            uploadRef.current?.upload?.uploader?.fileInput?.click()
          }
        >
          Upload Sheet
        </Button>
      </Dragger>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="default" tip="Loading paysheets..." />
        </div>
      ) : (
        <>
          <div
            className="d-flex justify-content-between align-items-center mt-4 mb-3"
            style={{ gap: "10px", flexWrap: "wrap" }}
          >
            <Text strong style={{ fontSize: "16px" }}>
              Uploaded Files ({files.length})
            </Text>

            <Input
              placeholder="Search files..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "280px",
                borderRadius: "8px",
              }}
            />
          </div>

          <div
            className="user-upload-list"
            style={{
              border: `1px solid ${token.colorBorder}`,
              borderRadius: "10px",
              padding: "16px",
              background: token.colorBgContainer,
            }}
          >
            {filteredFiles.length > 0 ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                  gap: "16px",
                }}
              >
                {filteredFiles.map((file) => (
                  <div
                    className="user-upload-file"
                    key={file.uid}
                    style={{
                      border: `1px solid ${token.colorBorder}`,
                      borderRadius: "12px",
                      padding: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      background: token.colorFillAlter,
                      transition: "0.2s",
                    }}
                  >
                    {/* Left Side */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        flex: 1,
                        overflow: "hidden",
                      }}
                    >
                      {/* File Icon */}
                      <div>
                        {file.type === "pdf" ? (
                          <FilePdfOutlined
                            style={{
                              fontSize: "32px",
                              color: "#ff4d4f",
                            }}
                          />
                        ) : (
                          <FileExcelOutlined
                            style={{
                              fontSize: "32px",
                              color: "#52c41a",
                            }}
                          />
                        )}
                      </div>

                      {/* File Details */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                        }}
                      >
                        <Text
                          ellipsis
                          style={{
                            fontWeight: 600,
                            fontSize: "14px",
                            maxWidth: "180px",
                          }}
                        >
                          {file.name}
                        </Text>

                        <Text
                          type="secondary"
                          style={{
                            fontSize: "12px",
                            marginTop: "2px",
                          }}
                        >
                          {file.uploadedDate}
                        </Text>
                      </div>
                    </div>

                    {/* Delete Icon */}
                    <DeleteOutlined
                      onClick={() => handleDeleteClick(file.uid)}
                      style={{
                        color: "#ff4d4f",
                        fontSize: "18px",
                        cursor: "pointer",
                        marginLeft: "12px",
                      }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: "40px" }}>
                <Empty description="No files found" />
              </div>
            )}
          </div>
        </>
      )}

      <RSModal className="user-upload-delete-modal" isOpen={isDeleteModalOpen} toggle={cancelDelete} centered>
        <ModalHeader toggle={cancelDelete}>Confirm Deletion</ModalHeader>

        <ModalBody>Are you sure you want to delete this sheet?</ModalBody>

        <ModalFooter>
          <Button danger onClick={proceedDelete}>
            Yes
          </Button>

          <Button onClick={cancelDelete}>No</Button>
        </ModalFooter>
      </RSModal>
    </div>
  );
};

export default FileUploads;
